import { Dialog, Transition } from '@headlessui/react';
import {
  cn,
  filterByInvoicingFeatures,
  SIDEBAR_LINKS,
  useInvoicingConfig,
} from '@jobcost-monorepo/utils';
import React, { Fragment, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Divider, LogoImage, useAuth } from '../../../atoms';
import SidebarMenuItem, { CardProps } from '../SidebarMenuItem';
import '../styles.css';
import { SubListProps } from '../subListCard';

type Props = {
  sidebarOpen: boolean;
  setSidebarOpen: (value: boolean) => void;
};

const MobileSidebar: React.FC<Props> = ({ sidebarOpen, setSidebarOpen }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const { config: invoicingConfig } = useInvoicingConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.split('/')[1];
  const [openedSubList, setOpenedSubList] = useState<number | undefined>(
    undefined,
  );

  const filterLinks = (links: CardProps[], isAdmin: boolean): CardProps[] => {
    if (isAdmin) {
      return links;
    }
    return links
      .filter((link) => !link.adminOnly)
      .map((link) => ({
        ...link,
        subList: link.subList ? filterSubList(link.subList) : undefined,
      }))
      .filter((link) => link.subList === undefined || link.subList.length > 0);
  };

  const filterSubList = (subList: SubListProps[]): SubListProps[] => {
    return subList.filter((subLink) => !subLink.adminOnly);
  };

  const handleNavigate = (item: string) => {
    setSidebarOpen(false);
    navigate(item);
  };

  const onClickLink = (item: CardProps, index: number) => {
    // If the item has a navigateTo prop, navigate to that route (not a subList)
    if (item.navigateTo) {
      return handleNavigate(item.navigateTo as string);
    }

    // If the openedSubList is the same as the index, close the subList
    // Otherwise, open the subList
    return openedSubList === index
      ? setOpenedSubList(undefined)
      : setOpenedSubList(index);
  };

  return (
    <Transition.Root show={sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50 lg:hidden"
        onClose={() => setSidebarOpen(false)}
      >
        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
              <div
                className={cn(
                  'h-[100%] flex flex-col shadow shadow-purple50 dark:shadow-extraDark50 rounded-br-3xl bg-white dark:bg-darkGrey w-[260px] p-6',
                )}
              >
                <div className="relative">
                  <LogoImage
                    className="w-full black fill-black dark:fill-white"
                    isSmall
                  />
                  <Divider />
                </div>
                <div
                  className={cn(
                    'flex flex-1 flex-col space-y-3 overflow-y-auto scrollbar-left -ml-3 -mr-6',
                  )}
                  style={{ scrollbarGutter: 'stable' }}
                >
                  {filterLinks(
                    filterByInvoicingFeatures(
                      SIDEBAR_LINKS,
                      invoicingConfig?.features,
                    ),
                    isAdmin,
                  ).map((item, index) => (
                    <SidebarMenuItem
                      key={`${item.label}_${index}`}
                      iconName={item.iconName}
                      label={item.label}
                      onClick={() => onClickLink(item, index)}
                      subList={item.subList}
                      isOpenSidebar={true}
                      setIsOpenSidebar={() => void 0}
                      isOpenSublist={index === openedSubList}
                      isSelected={item.navigateTo === path}
                      setSidebarOpenMobile={setSidebarOpen}
                    />
                  ))}
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MobileSidebar;
